'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, LogIn } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type {
  HouseholdMember,
  JoinField,
  JoinFormData,
  LocationCongregation,
  LocationGroup,
} from '@/lib/types';
import { InquiryError, fetchHousehold, fetchJoinForm, submitJoinForm } from '@/lib/api';
import { getMpWidgetToken, onMpTokenChange, signInAndReturn } from '@/lib/campus';
import { Avatar } from './Avatar';
import { JoinFieldControl } from './JoinFields';
import { SheetHeader } from './SheetHeader';

/**
 * "Help me find a group": MP's Join a Group form (Forms 2113) in a sheet,
 * drawn with our controls and filled in with what the finder already
 * knows (the campus, the filters they set). The response lands in MP as
 * the stock widget wrote it, so the campus Life Group Director still gets
 * their task. Signed in only, with the same sign-in-and-come-back flow as
 * the group inquiry form: the draft waits in session storage.
 */

/** What the finder already knows, keyed by the MP field's label. */
export interface JoinPrefill {
  campusId: number | null;
  /** Field label -> option (or options for a multiselect) */
  answers: Record<string, string | string[]>;
}

interface JoinSheetProps {
  open: boolean;
  onClose: () => void;
  apiBaseUrl: string;
  locations: LocationGroup[];
  prefill?: JoinPrefill;
}

interface Draft {
  answers: Record<number, string>;
  checks: Record<number, string>;
  campusId: number | null;
  pendingSend: boolean;
  at: number;
}
const DRAFT_KEY = 'group-finder:join-draft';
const DRAFT_MAX_AGE_MS = 30 * 60 * 1000;

function readDraft(): Draft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<Draft>;
    if (!d.at || Date.now() - d.at > DRAFT_MAX_AGE_MS) {
      sessionStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return {
      answers: d.answers ?? {},
      checks: d.checks ?? {},
      campusId: d.campusId ?? null,
      pendingSend: Boolean(d.pendingSend),
      at: d.at,
    };
  } catch {
    return null;
  }
}
function writeDraft(d: Draft) {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    /* storage blocked */
  }
}
function clearDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to clear */
  }
}
/** A sign-in round trip left a request waiting: the finder reopens the sheet. */
export function hasPendingJoin(): boolean {
  return Boolean(readDraft()?.pendingSend);
}

const key = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\bcampus\b/g, '')
    .replace(/[^a-z0-9]/g, '');

const CTA =
  'inline-flex items-center justify-center gap-1.5 rounded-full bg-[#299c8f] px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100';

export function JoinSheet({ open, onClose, apiBaseUrl, locations, prefill }: JoinSheetProps) {
  const [form, setForm] = useState<JoinFormData | null>(null);
  const [formError, setFormError] = useState(false);
  const [token, setToken] = useState<string | null>(() => getMpWidgetToken());
  const [self, setSelf] = useState<HouseholdMember | null>(null);
  const [draft] = useState<Draft | null>(() => readDraft());
  const [answers, setAnswers] = useState<Record<number, string>>(draft?.answers ?? {});
  const [checks, setChecks] = useState<Record<number, string>>(draft?.checks ?? {});
  const [campusId, setCampusId] = useState<number | null>(draft?.campusId ?? null);
  const [pendingSend, setPendingSend] = useState(Boolean(draft?.pendingSend));
  const [website, setWebsite] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ campusName: string | null } | null>(null);
  const prefilled = useRef(Boolean(draft));

  useEffect(() => onMpTokenChange(setToken), []);

  // The form definition, once the sheet first opens.
  useEffect(() => {
    if (!open || form) return;
    const controller = new AbortController();
    fetchJoinForm(apiBaseUrl, controller.signal)
      .then((f) => {
        if (!controller.signal.aborted) setForm(f);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFormError(true);
      });
    return () => controller.abort();
  }, [open, form, apiBaseUrl]);

  useEffect(() => {
    if (!token) {
      setSelf(null);
      return;
    }
    const controller = new AbortController();
    fetchHousehold(apiBaseUrl, controller.signal).then((m) => {
      if (!controller.signal.aborted) setSelf(m?.find((x) => x.isSelf) ?? null);
    });
    return () => controller.abort();
  }, [apiBaseUrl, token]);

  const fields = useMemo(() => form?.fields ?? [], [form]);
  const top = useMemo(() => fields.filter((f) => f.parentId === null), [fields]);
  const childrenOf = (f: JoinField) => fields.filter((c) => c.parentId === f.id);
  const campusField = useMemo(
    () => fields.find((f) => f.config?.component === 'CampusPicker' || /^campus$/i.test(f.label)),
    [fields]
  );
  const campusOption = (id: number | null): string => {
    if (!id || !campusField) return '';
    const cong = locations.flatMap((l) => l.congregations).find((c) => c.id === id);
    if (!cong) return '';
    const want = key(cong.name);
    return campusField.options.find((o) => key(o) === want) ?? '';
  };

  // What the finder knows goes in once, the first time the form arrives,
  // and never over something they typed or a draft they are returning to.
  useEffect(() => {
    if (!open || !form || prefilled.current) return;
    prefilled.current = true;
    const byLabel = new Map(fields.map((f) => [key(f.label), f]));
    const nextAnswers: Record<number, string> = {};
    const nextChecks: Record<number, string> = {};
    for (const [label, want] of Object.entries(prefill?.answers ?? {})) {
      const f = byLabel.get(key(label));
      if (!f) continue;
      if (f.kind === 'instructions' && f.isMultiselect) {
        const wanted = new Set((Array.isArray(want) ? want : [want]).map(key));
        for (const c of childrenOf(f)) if (wanted.has(key(c.label))) nextChecks[c.id] = 'Yes';
      } else if (typeof want === 'string') {
        const match = f.options.length ? f.options.find((o) => key(o) === key(want)) : want;
        if (match) nextAnswers[f.id] = match;
      }
    }
    const campus = campusOption(prefill?.campusId ?? null);
    if (campus && campusField) {
      nextAnswers[campusField.id] = campus;
      setCampusId((cur) => cur ?? prefill?.campusId ?? null);
    }
    setAnswers((cur) => ({ ...nextAnswers, ...cur }));
    setChecks((cur) => ({ ...nextChecks, ...cur }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form]);

  const onCampus = (cong: LocationCongregation | null) => {
    setCampusId(cong?.id ?? null);
    if (campusField)
      setAnswers((a) => ({ ...a, [campusField.id]: campusOption(cong?.id ?? null) }));
  };

  const missing = top.filter(
    (f) =>
      f.required && f.kind !== 'instructions' && f.kind !== 'file' && !(answers[f.id] ?? '').trim()
  );
  const canSend = form !== null && missing.length === 0;

  const send = async () => {
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      const result = await submitJoinForm(apiBaseUrl, {
        answers: { ...checks, ...answers },
        website,
      });
      clearDraft();
      setPendingSend(false);
      setSent({ campusName: result.campusName });
    } catch (err) {
      setPendingSend(false);
      // The sign-in ran out and MP would not renew it: sign in and send on return.
      if (err instanceof InquiryError && err.status === 401) {
        setToken(null);
        signInToSend();
      } else setError(err instanceof Error ? err.message : 'Could not send your request');
    } finally {
      setSending(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    if (!token) {
      signInToSend();
      return;
    }
    void send();
  };

  function signInToSend() {
    writeDraft({ answers, checks, campusId, pendingSend: true, at: Date.now() });
    setPendingSend(true);
    signInAndReturn();
  }

  // Back from sign-in with Send already pressed: finish it.
  useEffect(() => {
    if (!pendingSend || !token || sending || sent || !form) return;
    void send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSend, token, form]);

  const close = () => {
    if (sent) {
      setSent(null);
      setAnswers({});
      setChecks({});
      prefilled.current = false;
    }
    onClose();
  };

  return (
    <ResponsiveSheet
      open={open}
      onClose={close}
      maxWidth="max-w-3xl"
      noPanelPadding
      panelClassName="bg-white"
      header={
        <SheetHeader
          title="Help me find a group"
          subtitle={
            form?.instructions ??
            'Tell us a little about yourself and what you are looking for, and we will help you find the right group.'
          }
        />
      }
    >
      <SheetPage name="main">
        <div className="px-6 py-7 sm:px-8 sm:py-8">
          {sent ? (
            <div className="flex flex-col items-start gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#299c8f] text-white">
                <Check className="h-6 w-6" strokeWidth={3} />
              </span>
              <p className="text-base font-bold text-[#1c2b39]">Got it. We're on it.</p>
              <p className="text-sm text-gray-600">
                {sent.campusName
                  ? `The ${sent.campusName} life groups team has your request and will reach out to help you find a group.`
                  : 'The life groups team has your request and will reach out to help you find a group.'}
                {self?.email
                  ? ` They'll use ${self.email}${self.phone ? ` or ${self.phone}` : ''}.`
                  : ''}
              </p>
              <button type="button" onClick={close} className={`${CTA} mt-2`}>
                Done
              </button>
            </div>
          ) : formError ? (
            <p className="text-sm text-gray-600">
              We couldn't load the form right now. Please try again in a moment.
            </p>
          ) : !form ? (
            <div className="flex flex-col gap-6" aria-busy="true">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col gap-3">
                  <div className="h-3 w-32 animate-pulse rounded-full bg-gray-100" />
                  <div className="h-10 w-full max-w-md animate-pulse rounded-full bg-gray-100" />
                </div>
              ))}
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-7">
              <div className="flex flex-col gap-3">
                <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">
                  Who's asking?
                </span>
                {token && self ? (
                  <div className="flex items-center gap-3">
                    <Avatar name={self.name} imageUrl={self.imageUrl} size={36} />
                    <div className="min-w-0 text-sm">
                      <p className="font-bold text-[#1c2b39]">{self.name}</p>
                      <p className="truncate text-xs text-gray-500">
                        {[self.email, self.phone].filter(Boolean).join(' · ') ||
                          'Add an email or phone to your profile so we can reach you.'}
                      </p>
                    </div>
                  </div>
                ) : token ? (
                  <div className="h-9 w-56 animate-pulse rounded-full bg-gray-100" />
                ) : (
                  <p className="text-sm text-gray-600">
                    You. You'll sign in on the way so we know who to reach, and we'll send this the
                    moment you're back.
                  </p>
                )}
              </div>

              {top.map((f) => (
                <JoinFieldControl
                  key={f.id}
                  field={f}
                  choices={childrenOf(f)}
                  value={answers[f.id] ?? ''}
                  onChange={(v) => setAnswers((a) => ({ ...a, [f.id]: v }))}
                  childValues={checks}
                  onChildChange={(id, v) => setChecks((c) => ({ ...c, [id]: v }))}
                  locations={locations}
                  campusId={campusId}
                  onCampus={onCampus}
                />
              ))}

              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />

              {error && <p className="text-sm font-semibold text-red-600">{error}</p>}

              <div className="flex flex-wrap items-center gap-3">
                <button type="submit" disabled={!canSend || sending} className={CTA}>
                  {sending ? 'Sending…' : !token ? 'Sign in and send' : 'Send my request'}
                  {!sending && !token && <LogIn className="h-4 w-4" strokeWidth={2.5} />}
                  {!sending && token && <ChevronRight className="h-4 w-4" strokeWidth={2.5} />}
                </button>
                {missing.length > 0 && (
                  <span className="text-xs text-gray-500">
                    {missing.length === 1
                      ? `Pick ${(missing[0].config?.title ?? missing[0].label).replace(/\?$/, '').toLowerCase()}.`
                      : 'A few answers are still needed.'}
                  </span>
                )}
              </div>
            </form>
          )}
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
